import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export const supportedLocales = ["pt-BR", "en"] as const;
export type AppLocale = (typeof supportedLocales)[number];

const ptBR = {
  "common.actions": "Ações",
  "common.add": "Adicionar",
  "common.back": "Voltar",
  "common.active": "Ativo",
  "common.activate": "Ativar",
  "common.cancel": "Cancelar",
  "common.close": "Fechar",
  "common.copy": "Copiar",
  "common.description": "Descrição",
  "common.created": "Criado",
  "common.date": "Data",
  "common.delete": "Excluir",
  "common.deactivate": "Desativar",
  "common.deleteConfirm": "Excluir {{name}}?",
  "common.edit": "Editar",
  "common.email": "E-mail",
  "common.expires": "Expira",
  "common.inactive": "Inativo",
  "common.name": "Nome",
  "common.never": "Nunca",
  "common.password": "Senha",
  "common.pending": "Pendente",
  "common.revoked": "Revogado",
  "common.save": "Salvar",
  "common.status": "Status",
  "common.system": "sistema",
  "common.used": "Usado",
  "common.expired": "Expirado",
  "common.version": "Versão",
  "common.loading": "Carregando…",
  "common.operationCancelled": "Operação cancelada.",
  "common.confirmPassword": "Confirme sua senha",
  "common.search": "Buscar",
  "common.retry": "Tentar novamente",
  "common.noRecords": "Nenhum registro",
  "common.noRecordsDescription": "Adicione o primeiro registro para começar.",
  "common.showPassword": "Mostrar senha",
  "common.hidePassword": "Ocultar senha",
  "common.language": "Idioma",
  "theme.enableDark": "Ativar tema escuro",
  "theme.enableLight": "Ativar tema claro",
  "table.columns": "Colunas",
  "table.reset": "Restaurar padrão",
  "table.dragColumn": "Arrastar coluna {{name}}",
  "table.sortAscending": "Ordenar {{name}} em ordem crescente",
  "table.sortDescending": "Ordenar {{name}} em ordem decrescente",
  "table.unsorted": "Sem ordenação",
  "table.ascending": "Crescente",
  "table.descending": "Decrescente",
  "table.saving": "Salvando preferências da tabela",
  "table.saveFailed": "Não foi possível salvar as preferências da tabela.",
  "errors.pageLoadTitle": "Não foi possível carregar esta página",
  "errors.pageLoadDescription":
    "A aplicação pode ter sido atualizada. Recarregue para usar a versão mais recente.",
  "errors.reloadPage": "Recarregar página",
  "errors.reloadGuarded": "Finalize a atividade em andamento e atualize",
  "errors.sessionUnavailable": "Não foi possível verificar sua sessão",
  "errors.RATE_LIMITED":
    "Muitas requisições foram feitas em pouco tempo. Aguarde um instante e tente novamente; sua sessão continua ativa.",
  "errors.META_RATE_LIMITED":
    "A Meta limitou temporariamente as consultas desta conta. Aguarde alguns minutos e tente novamente.",
  "errors.META_TOKEN_INVALID":
    "O token de acesso da Meta está inválido ou expirou. Atualize a chave em Gerenciar contas.",
  "language.pt-BR": "Português",
  "language.en": "English",

  "nav.overview": "Visão geral",
  "nav.users": "Usuários",
  "nav.groups": "Grupos",
  "nav.leads": "Leads",
  "nav.metaAds": "Meta Ads",
  "nav.metaAdsAccounts": "Contas de anúncios",
  "nav.apiKeys": "Chaves de API",
  "nav.webhooks": "Webhooks",
  "nav.plugins": "Plugins",
  "nav.audit": "Auditoria",
  "nav.settings": "Configurações gerais",
  "nav.main": "Navegação principal",
  "nav.closeMenu": "Fechar menu",
  "nav.openMenu": "Abrir menu",
  "nav.collapseMenu": "Ocultar menu lateral",
  "nav.expandMenu": "Exibir menu lateral",
  "nav.panel": "Painel",
  "nav.signOut": "Sair",

  "auth.checking": "Verificando instalação…",
  "auth.setupTitle": "Configuração inicial",
  "auth.setupDescription": "Crie o primeiro administrador",
  "auth.name": "Nome",
  "auth.password": "Senha",
  "auth.confirmPassword": "Confirmar senha",
  "auth.finishSetup": "Concluir configuração",
  "auth.setupSuccess": "Administrador criado. Faça seu login.",
  "auth.setupFailure": "Falha na configuração.",
  "auth.passwordMin": "Use pelo menos 8 caracteres.",
  "auth.passwordMismatch": "As senhas não coincidem.",
  "auth.nameMin": "Informe pelo menos 2 caracteres.",
  "auth.invalidEmail": "Informe um e-mail válido.",
  "auth.loginTitle": "Acesse sua conta",
  "auth.loginDescription": "Não há cadastro público",
  "auth.signIn": "Entrar",
  "auth.invalidCredentials": "E-mail ou senha inválidos.",
  "auth.signInFailure": "Não foi possível entrar.",
  "auth.inviteTitle": "Aceitar convite",
  "auth.validatingInvite": "Validando convite…",
  "auth.missingInvite": "Convite ausente.",
  "auth.accountCreated": "Conta criada.",
  "auth.acceptFailure": "Não foi possível aceitar.",
  "auth.createAccount": "Criar conta",

  "dashboard.title": "Visão geral",
  "dashboard.description": "Acesse rapidamente o que você pode operar.",
  "dashboard.usersDescription": "Convites e acessos",
  "dashboard.groupsDescription": "Permissões e equipes",
  "dashboard.apiKeysDescription": "Credenciais de integração",
  "dashboard.leadsDescription": "Pipeline do CRM",
  "dashboard.webhooksDescription": "Entregas e tentativas",
  "dashboard.pluginsDescription": "Módulos instalados",
  "dashboard.auditDescription": "Histórico de atividades",
  "dashboard.settingsDescription": "Preferências e atualizações",
  "dashboard.searchModules": "Buscar plugins e páginas",
  "dashboard.openPlugin": "Abrir plugin",
  "dashboard.pluginWithoutPage": "Plugin sem página disponível",
  "dashboard.noResults": "Nenhum card corresponde à busca.",
  "dashboard.dragCard": "Arrastar {{name}} para reordenar",
  "dashboard.savingOrder": "Salvando ordem da visão geral",
  "dashboard.orderSaveFailed":
    "Não foi possível salvar a ordem da visão geral.",

  "users.description": "Contas, grupos e convites.",
  "users.search": "Buscar por nome ou e-mail",
  "users.groups": "Grupos",
  "users.created": "Usuário criado.",
  "users.updated": "Usuário atualizado.",
  "users.removed": "Usuário removido do acesso.",
  "users.removePassword": "Confirme sua senha para remover este usuário:",
  "users.removeConfirm": "Remover o acesso de {{name}}?",
  "users.active": "Usuário ativo",
  "users.newPassword": "Nova senha",
  "users.passwordUnchanged": "Deixe em branco para manter",
  "users.passwordHelp": "Opcional, com no mínimo 8 caracteres.",
  "users.changePasswordConfirmation":
    "Confirme sua senha para alterar a senha deste usuário:",
  "users.recentInvites": "Convites recentes",
  "users.noInvites": "Nenhum convite",
  "users.noInvitesDescription": "Use Convidar para enviar um convite.",
  "users.invite": "Convite",
  "users.inviteAction": "Convidar",
  "users.inviteCreated": "Convite criado e copiado.",
  "users.inviteRevoked": "Convite revogado.",
  "users.revokeConfirm": "Revogar o convite de {{email}}?",
  "users.revoke": "Revogar",
  "users.addTitle": "Adicionar usuário",
  "users.createDescription":
    "Cadastre todos os dados e defina o acesso inicial.",
  "users.addDescription": "Crie um convite de uso único válido por 48 horas.",
  "users.initialGroups": "Grupos iniciais",
  "users.createInvite": "Criar e copiar convite",
  "users.tabs.general": "Geral",
  "users.tabs.schedule": "Horários",
  "users.tabs.history": "Histórico",
  "users.phone": "Telefone",
  "users.telegramId": "ID Telegram",
  "users.jobTitle": "Cargo",
  "users.birthDate": "Data de nascimento",
  "users.cpf": "CPF",
  "users.permissionGroups": "Grupos de permissão",
  "users.tags": "Tags",
  "users.sectors": "Setores",
  "users.addTag": "Adicionar tag",
  "users.addSector": "Adicionar setor",
  "users.searchOrCreateTag": "Buscar ou criar tag…",
  "users.searchOrCreateSector": "Buscar ou criar setor…",
  "users.createOption": "Criar “{{value}}”",
  "users.noProfileOptions": "Nenhuma opção cadastrada.",
  "users.notes": "Observações",
  "users.adminOnly": "somente administradores",
  "users.notesPlaceholder": "Observações internas sobre o usuário…",
  "users.status.active": "Ativo",
  "users.status.inactive": "Inativo",
  "users.status.pending": "Aguardando ativação",
  "users.dailyHours": "Horas diárias",
  "users.entryTime": "Hora de entrada",
  "users.days.mon": "SEG",
  "users.days.tue": "TER",
  "users.days.wed": "QUA",
  "users.days.thu": "QUI",
  "users.days.fri": "SEX",
  "users.days.sat": "SÁB",
  "users.days.sun": "DOM",
  "users.scheduleHelp":
    "Cada alteração nos horários cria uma nova vigência a partir do momento em que for salva.",
  "users.historyHelp":
    "A jornada vale a partir da data do registro e não altera períodos anteriores.",
  "users.effective": "Vigência",
  "users.weekTotal": "Total/sem",
  "users.registered": "Registrado",
  "users.initial": "Inicial",
  "users.noScheduleHistory": "Nenhuma jornada registrada.",

  "groups.description": "Permissões agrupadas por função.",
  "groups.search": "Buscar grupo",
  "groups.members": "Membros",
  "groups.permissions": "Permissões",
  "groups.permissionCount": "{{count}} permissões",
  "groups.type": "Tipo",
  "groups.protected": "Protegido",
  "groups.custom": "Personalizado",
  "groups.saved": "Grupo salvo.",
  "groups.deleted": "Grupo excluído.",
  "groups.deletePassword": "Confirme sua senha para excluir este grupo:",
  "groups.deleteConfirm": "Excluir {{name}}?",
  "groups.addTitle": "Adicionar grupo",
  "groups.group": "Grupo",
  "groups.administrators": "Administradores",
  "groups.protectedDescription": "Este grupo é protegido.",
  "groups.formDescription": "Defina o nome e as permissões.",

  "permissions.core.user.read": "Visualizar usuários",
  "permissions.core.user.create": "Convidar e criar usuários",
  "permissions.core.user.update": "Editar usuários",
  "permissions.core.user.delete": "Remover usuários e revogar convites",
  "permissions.core.group.read": "Visualizar grupos",
  "permissions.core.group.create": "Criar grupos",
  "permissions.core.group.update": "Editar grupos",
  "permissions.core.group.delete": "Excluir grupos",
  "permissions.core.plugin.read": "Visualizar plugins",
  "permissions.core.plugin.create": "Instalar plugins",
  "permissions.core.plugin.update": "Atualizar plugins instalados",
  "permissions.core.plugin.delete": "Desinstalar plugins",
  "permissions.core.plugin.export": "Baixar pacotes de plugins",
  "permissions.core.marketplace.read": "Visualizar marketplaces",
  "permissions.core.marketplace.create": "Adicionar marketplaces",
  "permissions.core.marketplace.update": "Atualizar marketplaces",
  "permissions.core.marketplace.delete": "Remover marketplaces",
  "permissions.core.webhook.read": "Visualizar webhooks",
  "permissions.core.webhook.create": "Criar webhooks",
  "permissions.core.webhook.update": "Editar webhooks e trocar segredos",
  "permissions.core.webhook.delete": "Excluir webhooks",
  "permissions.core.webhook.test": "Enviar webhooks de teste",
  "permissions.core.webhook.redeliver": "Reenviar webhooks com falha",
  "permissions.core.audit.read": "Visualizar histórico de auditoria",
  "permissions.core.settings.read": "Visualizar configurações gerais",
  "permissions.core.settings.update": "Atualizar o Nexus Edge",
  "permissions.crm.lead.read": "Visualizar leads",
  "permissions.crm.lead.create": "Criar leads",
  "permissions.crm.lead.update": "Editar leads",
  "permissions.crm.lead.delete": "Excluir leads",
  "permissions.meta_ads.account.read": "Visualizar contas de anúncios",
  "permissions.meta_ads.account.create": "Adicionar contas de anúncios",
  "permissions.meta_ads.account.update": "Editar contas de anúncios",
  "permissions.meta_ads.account.delete": "Excluir contas de anúncios",
  "permissions.meta_ads.campaign.read": "Visualizar campanhas da Meta",
  "permissions.meta_ads.campaign.update": "Pausar e ativar campanhas da Meta",
  "permissions.meta_ads.adset.read": "Visualizar conjuntos de anúncios",
  "permissions.meta_ads.adset.update": "Pausar e ativar conjuntos de anúncios",
  "permissions.meta_ads.ad.read": "Visualizar anúncios da Meta",
  "permissions.meta_ads.ad.update": "Pausar e ativar anúncios da Meta",
  "permissions.meta_ads.insight.read": "Visualizar métricas do Meta Ads",
  "permissions.soletrando.child.read": "Visualizar crianças e treinos",
  "permissions.soletrando.child.create": "Cadastrar crianças",
  "permissions.soletrando.child.update": "Editar crianças e renovar links",
  "permissions.soletrando.child.delete": "Excluir crianças e históricos",
  "permissions.additional": "Permissão adicional do módulo",
  "permissionGroups.core.user": "Usuários",
  "permissionGroups.core.group": "Grupos e acessos",
  "permissionGroups.core.plugin": "Plugins",
  "permissionGroups.core.marketplace": "Marketplaces de plugins",
  "permissionGroups.core.webhook": "Webhooks",
  "permissionGroups.core.audit": "Auditoria",
  "permissionGroups.core.settings": "Configurações gerais",
  "permissionGroups.crm.lead": "CRM — Leads",
  "permissionGroups.meta_ads.account": "Meta Ads — Contas",
  "permissionGroups.meta_ads.campaign": "Meta Ads — Campanhas",
  "permissionGroups.meta_ads.adset": "Meta Ads — Conjuntos",
  "permissionGroups.meta_ads.ad": "Meta Ads — Anúncios",
  "permissionGroups.meta_ads.insight": "Meta Ads — Métricas",
  "permissionGroups.soletrando.child": "Soletrando — Crianças",
  "permissionGroups.additional": "Outras permissões do módulo",

  "settings.title": "Configurações gerais",
  "settings.coreCardsTitle": "Mostrar menus do sistema como cards",
  "settings.coreCardsDescription":
    "Exibe os atalhos da sidebar na tela inicial. Os plugins continuam visíveis.",
  "settings.coreCardsLoadFailed":
    "Não foi possível carregar esta preferência. Atualize a página para tentar novamente.",
  "settings.coreCardsSaveFailed":
    "Não foi possível salvar a exibição dos cards.",
  "settings.description":
    "Informações da instalação e atualizações do Nexus Edge.",
  "settings.installationTitle": "Instalação",
  "settings.installationDescription":
    "Versão e ambiente atualmente em execução.",
  "settings.provider": "Banco de dados",
  "settings.channel": "Canal",
  "settings.channelBeta": "Beta",
  "settings.channelStable": "Estável",
  "settings.signature": "Assinatura",
  "settings.required": "Obrigatória",
  "settings.updatesTitle": "Atualizações",
  "settings.updatesDescription":
    "Busca releases beta assinadas diretamente no GitHub oficial.",
  "settings.updateNow": "Atualizar agora",
  "settings.continueUpdate": "Continuar atualização",
  "settings.confirmUpdatePassword":
    "Confirme sua senha para atualizar o Nexus Edge:",
  "settings.releaseDetails": "Ver release",
  "settings.noRelease": "Nenhuma release beta mais recente foi publicada.",
  "settings.sourceUnavailable":
    "Não foi possível consultar ou validar as releases do GitHub agora.",
  "settings.d1Only":
    "O atualizador beta automático está disponível somente para instalações com D1.",
  "settings.credentialRequired":
    "Cadastre primeiro a credencial limitada do Cloudflare.",
  "settings.openPlugins": "Abrir Plugins",
  "settings.backupNotice":
    "Antes de migrar, o Nexus registra o instante de restauração do D1 Time Travel. A atualização nunca aceita arquivos sem assinatura válida.",
  "settings.keepPageOpen":
    "Mantenha esta página aberta até a verificação terminar.",
  "settings.updateStageMigrating": "Aplicando migrações assinadas",
  "settings.updateStageDeploying": "Publicando Worker e interface",
  "settings.updateStageVerifying": "Verificando a nova versão",
  "settings.updateStageInstalled": "Atualização concluída",
  "settings.updateStageFailed": "Atualização interrompida",
  "settings.updateFailed":
    "A atualização foi interrompida com segurança. Tente novamente após verificar o diagnóstico.",
  "settings.updateInstalled": "Nexus Edge atualizado com sucesso.",
  "errors.CORE_UPDATE_STAGE_FAILED":
    "A etapa de atualização falhou e foi interrompida com segurança.",
  "errors.CORE_UPDATE_SOURCE_UNAVAILABLE":
    "Não foi possível validar as releases no GitHub.",
  "errors.CORE_UPDATE_CREDENTIAL_REQUIRED":
    "Cadastre a credencial limitada do Cloudflare na tela de Plugins.",
  "errors.CORE_UPDATE_NOT_AVAILABLE":
    "Esta instalação já está na versão mais recente.",
  "errors.CORE_UPDATE_PROVIDER_UNSUPPORTED":
    "O atualizador beta aceita somente instalações D1.",

  "apiKeys.description": "Credenciais pessoais para scripts e agentes.",
  "apiKeys.search": "Buscar chave",
  "apiKeys.unnamed": "Sem nome",
  "apiKeys.identifier": "Identificação",
  "apiKeys.active": "Ativa",
  "apiKeys.revoked": "Revogada",
  "apiKeys.createdSuccess": "Chave criada. Copie o segredo agora.",
  "apiKeys.revokedSuccess": "Chave revogada.",
  "apiKeys.revokeConfirm": "Revogar esta chave?",
  "apiKeys.deleteLabel": "Excluir chave",
  "apiKeys.titleSingle": "Chave de API",
  "apiKeys.lastUsed": "Último uso",
  "apiKeys.addTitle": "Adicionar chave",
  "apiKeys.addDescription": "O segredo completo será exibido uma única vez.",
  "apiKeys.copied": "Copiado.",
  "apiKeys.copySecret": "Copiar segredo",
  "apiKeys.validityDays": "Validade em dias",
  "apiKeys.scopes": "Escopos",
  "apiKeys.create": "Criar chave",
  "apiKeys.documentation": "Documentação da API",

  "webhooks.description": "Endpoints, eventos e entregas assinadas.",
  "webhooks.search": "Buscar endpoint",
  "webhooks.destination": "Destino",
  "webhooks.events": "Eventos",
  "webhooks.eventTypes": "{{count}} tipos",
  "webhooks.saved": "Webhook salvo.",
  "webhooks.deleted": "Webhook excluído.",
  "webhooks.deletePassword": "Confirme sua senha para excluir:",
  "webhooks.rotatePassword": "Confirme sua senha para rotacionar o segredo:",
  "webhooks.secretRotated": "Segredo rotacionado.",
  "webhooks.actionQueued": "Ação enfileirada.",
  "webhooks.redeliveryQueued": "Reentrega enfileirada.",
  "webhooks.test": "Testar",
  "webhooks.recentDeliveries": "Entregas recentes",
  "webhooks.noDeliveries": "Nenhuma entrega",
  "webhooks.noDeliveriesDescription": "As tentativas aparecerão aqui.",
  "webhooks.event": "Evento",
  "webhooks.attempts": "Tentativas",
  "webhooks.redeliver": "Reenviar entrega",
  "webhooks.deliveryStatus.delivered": "Entregue",
  "webhooks.deliveryStatus.failed": "Falhou",
  "webhooks.deliveryStatus.pending": "Pendente",
  "webhooks.addTitle": "Adicionar webhook",
  "webhooks.copyOnce": "Copie o segredo agora. Ele não será exibido novamente.",
  "webhooks.copySecret": "Copiar segredo",
  "webhooks.httpsUrl": "URL HTTPS",
  "webhooks.urlPlaceholder": "https://exemplo.com/webhook",
  "webhooks.enabled": "Endpoint ativo",
  "webhooks.rotate": "Rotacionar",

  "audit.description": "Ações administrativas e rastreabilidade por requestId.",
  "audit.search": "Buscar por ação",
  "audit.noEvents": "Nenhum evento",
  "audit.noEventsDescription": "As ações administrativas aparecerão aqui.",
  "audit.action": "Ação",
  "audit.resource": "Recurso",
  "audit.authentication": "Autenticação",
  "audit.record": "Registro de auditoria",
  "audit.user": "Usuário",
  "audit.metadata": "Metadados",

  "plugins.description":
    "Instale, atualize e gerencie plugins independentes e seus marketplaces.",
  "plugins.installedSection": "Instalados",
  "plugins.tabs.label": "Seções de plugins",
  "plugins.tabs.installed": "Instalados",
  "plugins.tabs.catalog": "Novos Plugins",
  "plugins.tabs.marketplaces": "Market Places",
  "plugins.explore": "Explorar",
  "plugins.exploreDescription":
    "Instale ou atualize diretamente de marketplaces GitHub verificados.",
  "plugins.searchMarketplace": "Buscar no catálogo",
  "plugins.publisher": "Editor",
  "plugins.marketplace": "Marketplace",
  "plugins.compatibility": "Compatibilidade",
  "plugins.installationStatus": "Instalação",
  "plugins.compatible": "Compatível",
  "plugins.incompatible": "Incompatível",
  "plugins.available": "Disponível",
  "plugins.alreadyInstalled": "Já instalado",
  "plugins.updateAvailable": "Atualização disponível",
  "plugins.updateFromFile": "Atualizar por arquivo",
  "plugins.changelog": "O que mudou nesta versão",
  "plugins.noChangelog": "Notas desta versão não disponíveis.",
  "plugins.updateVersions": "Versão {{current}} para {{next}}",
  "plugins.updateIncompatible":
    "Esta versão não é compatível com esta instalação.",
  "plugins.updateUnavailable":
    "Este marketplace precisa ser sincronizado novamente antes da atualização.",
  "plugins.packageSize": "Tamanho do pacote",
  "plugins.pluginDetails": "Detalhes do plugin",
  "plugins.catalogEmpty":
    "Nenhum plugin está disponível. Sincronize um marketplace para carregar o catálogo.",
  "plugins.marketplaces": "Marketplaces",
  "plugins.marketplacesDescription":
    "Fontes GitHub autorizadas para descoberta e atualização de plugins.",
  "plugins.addMarketplace": "Adicionar marketplace",
  "plugins.defaultMarketplace": "padrão",
  "plugins.repository": "Repositório GitHub",
  "plugins.trust": "Confiança",
  "plugins.lastSync": "Última sincronização",
  "plugins.syncMarketplace": "Sincronizar marketplace",
  "plugins.marketplaceAdded": "Marketplace adicionado.",
  "plugins.marketplaceUpdated": "Marketplace atualizado.",
  "plugins.marketplaceSynced":
    "Marketplace sincronizado e assinatura validada.",
  "plugins.marketplaceAutoSyncFailed":
    "Não foi possível atualizar um ou mais marketplaces. O catálogo exibido pode estar desatualizado.",
  "plugins.marketplaceRemoved":
    "Marketplace removido; os plugins já instalados foram preservados.",
  "plugins.removeMarketplaceConfirm": "Remover o marketplace {{name}}?",
  "plugins.marketplaceTrustNotice":
    "O catálogo será sincronizado automaticamente. A assinatura é validada e a primeira chave pública válida fica protegida nesta instalação, sem confirmação manual.",
  "plugins.marketplaceDetails":
    "Edite o nome, copie os dados ou altere a disponibilidade deste marketplace.",
  "plugins.copyMarketplaceName": "Copiar nome do marketplace",
  "plugins.copyMarketplaceRepository": "Copiar repositório do marketplace",
  "plugins.copied": "Copiado.",
  "plugins.copyFailed": "Não foi possível copiar.",
  "plugins.marketplaceDownloadFailed":
    "Não foi possível baixar e verificar o pacote do marketplace.",
  "plugins.recoveryModeTitle": "Modo de recuperação do Core",
  "plugins.recoveryModeDescription":
    "A interface dinâmica deste plugin não foi carregada. Abra Plugins para desativá-lo, atualizá-lo ou inspecionar sua origem.",
  "plugins.dynamicUnavailableTitle": "Interface do plugin indisponível",
  "plugins.dynamicUnavailableDescription":
    "O plugin não está instalado, você não tem acesso ou seus assets locais não estão disponíveis.",
  "plugins.dynamicFailedTitle": "A interface do plugin encontrou um erro",
  "plugins.openRecoveryMode": "Abrir modo de recuperação",
  "plugins.search": "Buscar plugin",
  "plugins.database": "Banco",
  "plugins.worker": "Worker",
  "plugins.installTitle": "Instalar ou atualizar plugin",
  "plugins.minimumCore": "Core mínimo",
  "plugins.rawSize": "Tamanho cru",
  "plugins.migrations": "Migrations",
  "plugins.permissions": "Permissões",
  "plugins.operation": "Operação {{id}}",
  "plugins.update": "Atualizar",
  "plugins.install": "Instalar",
  "plugins.uninstall": "Desinstalar",
  "plugins.downloadPackage": "Baixar pacote",
  "plugins.packageDownloaded": "Pacote do plugin baixado.",
  "plugins.downloadUnavailable":
    "Selecione o pacote original uma vez para validar e liberar o download.",
  "plugins.selectOriginalPackage":
    "Selecione o arquivo .plugin.zip original usado nesta instalação. Ele será validado sem reinstalar o plugin ou alterar dados.",
  "plugins.archivePackageMismatch":
    "O arquivo selecionado não corresponde ao plugin e à versão instalados.",
  "plugins.provider": "Provider",
  "plugins.invalidPackage": "Pacote inválido.",
  "plugins.permissionRequired":
    "Seu grupo não permite instalar ou atualizar este plugin.",
  "plugins.selectPackage": "Selecione um arquivo .plugin.zip.",
  "plugins.packageContents":
    "O pacote precisa conter manifest.json e worker.mjs.",
  "plugins.migrationPairs":
    "As migrations D1 e PostgreSQL devem existir aos pares.",
  "plugins.rawTooLarge": "O pacote cru excede 8 MiB.",
  "plugins.expansionTooLarge":
    "O pacote excede os limites seguros de expansão ou quantidade de arquivos.",
  "plugins.gzipTooLarge": "O worker compactado excede 3 MiB.",
  "plugins.installFailed":
    "A instalação falhou. Abra e copie o relatório de suporte abaixo.",
  "plugins.runtimeCredentialTitle": "Autorize a publicação do primeiro plugin",
  "plugins.runtimeCredentialBody":
    "Isso é necessário apenas uma vez para o Nexus publicar e atualizar os Workers dos seus plugins.",
  "plugins.runtimeCredentialCreate": "Criar token na Cloudflare",
  "plugins.runtimeCredentialOpenList": "Abrir lista de tokens desta conta",
  "plugins.runtimeCredentialStepOpen":
    "Clique no botão acima. A Cloudflare abrirá a conta correta com o nome e a permissão do token preenchidos.",
  "plugins.runtimeCredentialStepReview":
    "Confira se existe somente esta permissão:",
  "plugins.runtimeCredentialStepCreateToken":
    "Clique em Continuar para o resumo e depois em Criar token.",
  "plugins.runtimeCredentialStepCreate":
    "Na Cloudflare, clique em Create Token e escolha criar um Custom Token.",
  "plugins.runtimeCredentialStepPermission": "Adicione somente esta permissão:",
  "plugins.runtimeCredentialStepAccount":
    "Em Account Resources, inclua somente a conta:",
  "plugins.runtimeCredentialStepPaste":
    "Copie o token que aparecer e cole-o no campo abaixo.",
  "plugins.runtimeCredentialTargetAccount": "Conta selecionada:",
  "plugins.runtimeCredentialWarning":
    "A Cloudflare mostra o token apenas uma vez. Não o envie por mensagem nem o salve em documentos.",
  "plugins.runtimeCredentialLabel": "API Token dedicado",
  "plugins.runtimeCredentialPlaceholder": "Cole aqui o token da Cloudflare",
  "plugins.runtimeCredentialHelp":
    "O token será validado e gravado diretamente como secret deste Core Worker. Ele não será salvo no banco.",
  "plugins.runtimeCredentialLater": "Fazer depois",
  "plugins.runtimeCredentialSave": "Validar e salvar token",
  "plugins.runtimeCredentialContinue": "Validar token e instalar",
  "plugins.runtimeCredentialSaved": "Credencial de plugins configurada.",
  "plugins.runtimeCredentialSavedAndInstalling":
    "Credencial de plugins configurada. Iniciando instalação…",
  "plugins.runtimeCredentialActivationPending":
    "A Cloudflare salvou o secret, mas a nova versão do Worker ainda está ativando. Aguarde alguns segundos e tente instalar novamente.",
  "plugins.runtimeCredentialLoadFailed":
    "Não foi possível verificar a credencial de plugins.",
  "plugins.runtimeCredentialRetry": "Verificar novamente",
  "plugins.supportReport": "Relatório de suporte da instalação",
  "plugins.supportReportHelp":
    "Copie este diagnóstico seguro e envie ao desenvolvedor. Logs brutos, credenciais e segredos não são incluídos.",
  "plugins.copySupportReport": "Copiar relatório",
  "plugins.supportReportCopied": "Relatório de suporte copiado.",
  "plugins.supportReportCopyFailed":
    "Não foi possível copiar o relatório. Selecione o texto manualmente.",
  "plugins.installed": "Plugin instalado.",
  "plugins.activated": "Plugin ativado.",
  "plugins.deactivated": "Plugin desativado sem ser desinstalado.",
  "plugins.activateBeforeUpdate":
    "Este plugin está desativado. Ative-o antes de atualizar.",
  "plugins.uninstalled": "Plugin desinstalado; as tabelas foram preservadas.",
  "plugins.uninstallConfirm": "Desinstalar {{name}} {{version}}?",
  "plugins.recordDeleted":
    "Registro do plugin excluído; tabelas e histórico foram preservados.",
  "plugins.deleteRecordConfirm":
    "Excluir {{name}} da lista de plugins? As tabelas e o histórico serão preservados.",
  "plugins.state.validating": "Validando",
  "plugins.state.provisioning": "Provisionando recursos",
  "plugins.state.migrating": "Aplicando migrations",
  "plugins.state.deploying": "Publicando",
  "plugins.state.hardening": "Protegendo",
  "plugins.state.binding": "Vinculando",
  "plugins.state.registering": "Registrando",
  "plugins.state.installed": "Instalado",
  "plugins.state.disabled": "Desativado",
  "plugins.state.failed": "Falhou",
  "plugins.r2ProvisioningTitle": "Armazenamento privado R2",
  "plugins.r2ProvisioningDescription":
    "Este plugin precisa de um bucket próprio. Use um token temporário somente para criar e vincular esse bucket.",
  "plugins.r2ProvisioningStepPermission":
    "Em Meu perfil → Tokens de API, crie um token de usuário limitado à permissão Account → Workers R2 Storage → Edit nesta conta.",
  "plugins.r2ProvisioningStepPaste":
    "Cole o token abaixo e conclua a instalação.",
  "plugins.r2ProvisioningStepRevoke":
    "Revogue o token na Cloudflare depois que a instalação terminar.",
  "plugins.r2OpenTokens": "Abrir tokens de API do usuário",
  "plugins.r2TokenLabel": "Token temporário do R2",
  "plugins.r2TokenPlaceholder": "Cole o token temporário",
  "plugins.r2TokenPrivacy":
    "O token permanece apenas na memória desta tela e é descartado após o provisionamento.",
  "plugins.r2TokenRequired": "Informe o token temporário do R2.",
  "plugins.resourceProvisioningTitle": "Recursos privados do plugin",
  "plugins.resourceProvisioningDescription":
    "O manifesto solicita recursos Cloudflare isolados. O Core cria e registra cada recurso sem guardar o token administrativo.",
  "plugins.resourceProvisioningStepPermission":
    "Crie um token temporário limitado às permissões e aos produtos solicitados pelo manifesto.",
  "plugins.resourceProvisioningStepPaste":
    "Cole o token abaixo; o Core provisionará somente os recursos pendentes.",
  "plugins.resourceProvisioningStepRevoke":
    "Revogue o token na Cloudflare assim que a operação terminar.",
  "plugins.resourceOpenTokens": "Abrir tokens da conta Cloudflare",
  "plugins.resourceTokenLabel": "Token temporário dos recursos",
  "plugins.resourceTokenPlaceholder": "Cole o token temporário",
  "plugins.resourceTokenPrivacy":
    "O token é usado somente durante esta operação e não é salvo nem enviado ao plugin.",
  "plugins.resourceTokenRequired":
    "Informe um token temporário com acesso somente aos produtos solicitados pelo plugin.",
  "plugins.resourceReauthPassword":
    "Confirme sua senha para provisionar os recursos Cloudflare do plugin.",
  "plugins.resourcePlanInvalid":
    "O plano de recursos do plugin está incompleto ou inconsistente.",
  "plugins.marketplaceTrustTitle": "Confiar na chave do marketplace",
  "plugins.marketplaceTrustDescription":
    "Confira o fingerprint por um canal confiável e digite-o para fixar a identidade deste editor.",
  "plugins.marketplaceFingerprint": "Fingerprint apresentado",
  "plugins.marketplaceFingerprintConfirmation":
    "Confirme digitando o fingerprint completo",
  "plugins.marketplaceTrustConfirm": "Confiar e sincronizar",
  "plugins.marketplaceTrusted": "Marketplace confiado e sincronizado.",
  "plugins.marketplaceTrustInvalid": "Os dados da chave não estão disponíveis.",
  "plugins.marketplaceTrustReauthPassword":
    "Confirme sua senha para confiar na chave do marketplace.",
  "plugins.r2ReauthPassword": "Confirme sua senha para provisionar o R2",

  "errors.fallback": "Não foi possível concluir a operação.",
  "errors.BOOTSTRAP_UNAVAILABLE": "A configuração inicial já foi concluída.",
  "errors.BOOTSTRAP_ALREADY_CLAIMED":
    "A configuração inicial já foi reivindicada por outro e-mail.",
  "errors.BOOTSTRAP_RACE": "Outra solicitação concluiu esta etapa.",
  "errors.VALIDATION_ERROR": "Revise os dados informados.",
  "errors.UNAUTHORIZED": "Faça login para continuar.",
  "errors.UNAUTHENTICATED": "Faça login para continuar.",
  "errors.FORBIDDEN": "Você não tem permissão para esta operação.",
  "errors.PLUGIN_PACKAGE_EXPORT_NOT_INSTALLED":
    "Somente plugins instalados podem ser baixados.",
  "errors.PLUGIN_PACKAGE_EXPORT_UNAVAILABLE":
    "O pacote portátil não está disponível. Selecione o arquivo original para recuperá-lo com segurança.",
  "errors.PLUGIN_PACKAGE_ARCHIVE_MISMATCH":
    "O arquivo selecionado não é exatamente o pacote usado nesta instalação.",
  "errors.PLUGIN_PACKAGE_CONTAINS_RUNTIME_VALUE":
    "O pacote contém uma configuração ou credencial específica desta instalação e não pode ser instalado.",
  "errors.PLUGIN_CORE_VERSION_UNSUPPORTED":
    "Atualize o Core do Nexus antes de instalar esta versão do plugin.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_REQUIRED":
    "Configure o token limitado da Cloudflare antes de instalar o plugin.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_INVALID":
    "O token é inválido, pertence a outra conta ou não possui Workers Scripts Edit.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_TOO_BROAD":
    "Use um token dedicado com somente Workers Scripts Edit nesta conta.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_TARGET_MISSING":
    "A conta Cloudflare desta instalação não está configurada no Core.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_SAVE_FAILED":
    "A Cloudflare não conseguiu gravar o token como secret do Core Worker.",
  "errors.EMAIL_ALREADY_EXISTS": "Este e-mail já pertence a outro usuário.",
  "errors.PASSWORD_ACCOUNT_UNAVAILABLE":
    "Este usuário não possui uma conta com senha.",
  "errors.GROUP_UNAVAILABLE": "Um ou mais grupos selecionados não existem.",
  "errors.ADMIN_GROUP_FORBIDDEN":
    "Somente um administrador pode atribuir o grupo Administradores.",
  "errors.PERMISSION_ESCALATION":
    "Você não pode conceder permissões além das suas.",
  "errors.LAST_ADMIN":
    "O último administrador ativo deve permanecer ativo e no grupo Administradores.",
  "errors.REAUTH_REQUIRED": "Confirme sua senha para continuar.",
  "errors.REAUTH_INVALID": "A confirmação de senha expirou ou é inválida.",
  "errors.USER_NOT_FOUND": "Usuário não encontrado.",
  "errors.META_TOKEN_NOT_CONFIGURED":
    "O token da Meta ainda não foi configurado no Worker do plugin.",
  "errors.INVALID_AD_ACCOUNT_ID": "Informe um ID de conta válido.",
  "errors.AD_ACCOUNT_ALREADY_EXISTS": "Esta conta já está cadastrada.",
  "errors.AD_ACCOUNT_NOT_CONFIGURED":
    "A conta não está cadastrada ou está desativada.",
  "errors.INVALID_DATE_RANGE": "Revise o período selecionado.",
  "errors.DATE_RANGE_TOO_LARGE": "Selecione no máximo 366 dias.",
} as const;

export type TranslationKey = keyof typeof ptBR;

const en: Record<TranslationKey, string> = {
  "common.actions": "Actions",
  "common.add": "Add",
  "common.back": "Back",
  "common.active": "Active",
  "common.activate": "Activate",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.copy": "Copy",
  "common.description": "Description",
  "common.created": "Created",
  "common.date": "Date",
  "common.delete": "Delete",
  "common.deactivate": "Deactivate",
  "common.deleteConfirm": "Delete {{name}}?",
  "common.edit": "Edit",
  "common.email": "Email",
  "common.expires": "Expires",
  "common.inactive": "Inactive",
  "common.name": "Name",
  "common.never": "Never",
  "common.password": "Password",
  "common.pending": "Pending",
  "common.revoked": "Revoked",
  "common.save": "Save",
  "common.status": "Status",
  "common.system": "system",
  "common.used": "Used",
  "common.expired": "Expired",
  "common.version": "Version",
  "common.loading": "Loading…",
  "common.operationCancelled": "Operation cancelled.",
  "common.confirmPassword": "Confirm your password",
  "common.search": "Search",
  "common.retry": "Try again",
  "common.noRecords": "No records",
  "common.noRecordsDescription": "Add the first record to get started.",
  "common.showPassword": "Show password",
  "common.hidePassword": "Hide password",
  "common.language": "Language",
  "theme.enableDark": "Enable dark theme",
  "theme.enableLight": "Enable light theme",
  "table.columns": "Columns",
  "table.reset": "Restore defaults",
  "table.dragColumn": "Drag {{name}} column",
  "table.sortAscending": "Sort {{name}} ascending",
  "table.sortDescending": "Sort {{name}} descending",
  "table.unsorted": "Not sorted",
  "table.ascending": "Ascending",
  "table.descending": "Descending",
  "table.saving": "Saving table preferences",
  "table.saveFailed": "Unable to save table preferences.",
  "errors.pageLoadTitle": "This page could not be loaded",
  "errors.pageLoadDescription":
    "The application may have been updated. Reload to use the latest version.",
  "errors.reloadPage": "Reload page",
  "errors.reloadGuarded": "Finish the active task and update",
  "errors.sessionUnavailable": "Your session could not be verified",
  "errors.RATE_LIMITED":
    "Too many requests were made in a short time. Wait a moment and try again; your session remains active.",
  "errors.META_RATE_LIMITED":
    "Meta temporarily limited requests for this ad account. Wait a few minutes and try again.",
  "errors.META_TOKEN_INVALID":
    "The Meta access token is invalid or expired. Update it in Manage accounts.",
  "language.pt-BR": "Português",
  "language.en": "English",
  "nav.overview": "Overview",
  "nav.users": "Users",
  "nav.groups": "Groups",
  "nav.leads": "Leads",
  "nav.metaAds": "Meta Ads",
  "nav.metaAdsAccounts": "Ad accounts",
  "nav.apiKeys": "API keys",
  "nav.webhooks": "Webhooks",
  "nav.plugins": "Plugins",
  "nav.audit": "Audit",
  "nav.settings": "General settings",
  "nav.main": "Main navigation",
  "nav.closeMenu": "Close menu",
  "nav.openMenu": "Open menu",
  "nav.collapseMenu": "Hide sidebar",
  "nav.expandMenu": "Show sidebar",
  "nav.panel": "Dashboard",
  "nav.signOut": "Sign out",
  "auth.checking": "Checking installation…",
  "auth.setupTitle": "Initial setup",
  "auth.setupDescription": "Create the first administrator",
  "auth.name": "Name",
  "auth.password": "Password",
  "auth.confirmPassword": "Confirm password",
  "auth.finishSetup": "Finish setup",
  "auth.setupSuccess": "Administrator created. Sign in to continue.",
  "auth.setupFailure": "Setup failed.",
  "auth.passwordMin": "Use at least 8 characters.",
  "auth.passwordMismatch": "Passwords do not match.",
  "auth.nameMin": "Enter at least 2 characters.",
  "auth.invalidEmail": "Enter a valid email address.",
  "auth.loginTitle": "Sign in to your account",
  "auth.loginDescription": "Public registration is disabled",
  "auth.signIn": "Sign in",
  "auth.invalidCredentials": "Invalid email or password.",
  "auth.signInFailure": "Unable to sign in.",
  "auth.inviteTitle": "Accept invitation",
  "auth.validatingInvite": "Validating invitation…",
  "auth.missingInvite": "Invitation is missing.",
  "auth.accountCreated": "Account created.",
  "auth.acceptFailure": "Unable to accept the invitation.",
  "auth.createAccount": "Create account",
  "dashboard.title": "Overview",
  "dashboard.description": "Quickly access everything you can manage.",
  "dashboard.usersDescription": "Invitations and access",
  "dashboard.groupsDescription": "Permissions and teams",
  "dashboard.apiKeysDescription": "Integration credentials",
  "dashboard.leadsDescription": "CRM pipeline",
  "dashboard.webhooksDescription": "Deliveries and attempts",
  "dashboard.pluginsDescription": "Installed modules",
  "dashboard.auditDescription": "Activity history",
  "dashboard.settingsDescription": "Preferences and updates",
  "dashboard.searchModules": "Search plugins and pages",
  "dashboard.openPlugin": "Open plugin",
  "dashboard.pluginWithoutPage": "Plugin without an available page",
  "dashboard.noResults": "No cards match your search.",
  "dashboard.dragCard": "Drag {{name}} to reorder",
  "dashboard.savingOrder": "Saving overview order",
  "dashboard.orderSaveFailed": "Unable to save the overview order.",
  "users.description": "Accounts, groups, and invitations.",
  "users.search": "Search by name or email",
  "users.groups": "Groups",
  "users.created": "User created.",
  "users.updated": "User updated.",
  "users.removed": "User access removed.",
  "users.removePassword": "Confirm your password to remove this user:",
  "users.removeConfirm": "Remove access for {{name}}?",
  "users.active": "Active user",
  "users.newPassword": "New password",
  "users.passwordUnchanged": "Leave blank to keep it",
  "users.passwordHelp": "Optional, with at least 8 characters.",
  "users.changePasswordConfirmation":
    "Confirm your password to change this user's password:",
  "users.recentInvites": "Recent invitations",
  "users.noInvites": "No invitations",
  "users.noInvitesDescription": "Use Invite to send an invitation.",
  "users.invite": "Invitation",
  "users.inviteAction": "Invite",
  "users.inviteCreated": "Invitation created and copied.",
  "users.inviteRevoked": "Invitation revoked.",
  "users.revokeConfirm": "Revoke the invitation for {{email}}?",
  "users.revoke": "Revoke",
  "users.addTitle": "Add user",
  "users.createDescription": "Enter all details and set the initial access.",
  "users.addDescription": "Create a single-use invitation valid for 48 hours.",
  "users.initialGroups": "Initial groups",
  "users.createInvite": "Create and copy invitation",
  "users.tabs.general": "General",
  "users.tabs.schedule": "Schedule",
  "users.tabs.history": "History",
  "users.phone": "Phone",
  "users.telegramId": "Telegram ID",
  "users.jobTitle": "Job title",
  "users.birthDate": "Date of birth",
  "users.cpf": "CPF",
  "users.permissionGroups": "Permission groups",
  "users.tags": "Tags",
  "users.sectors": "Sectors",
  "users.addTag": "Add tag",
  "users.addSector": "Add sector",
  "users.searchOrCreateTag": "Search or create tag…",
  "users.searchOrCreateSector": "Search or create sector…",
  "users.createOption": "Create “{{value}}”",
  "users.noProfileOptions": "No options registered.",
  "users.notes": "Notes",
  "users.adminOnly": "administrators only",
  "users.notesPlaceholder": "Internal notes about the user…",
  "users.status.active": "Active",
  "users.status.inactive": "Inactive",
  "users.status.pending": "Awaiting activation",
  "users.dailyHours": "Daily hours",
  "users.entryTime": "Start time",
  "users.days.mon": "MON",
  "users.days.tue": "TUE",
  "users.days.wed": "WED",
  "users.days.thu": "THU",
  "users.days.fri": "FRI",
  "users.days.sat": "SAT",
  "users.days.sun": "SUN",
  "users.scheduleHelp":
    "Each schedule change creates a new effective version from the moment it is saved.",
  "users.historyHelp":
    "The schedule applies from its registration date and does not change earlier periods.",
  "users.effective": "Effective",
  "users.weekTotal": "Total/week",
  "users.registered": "Registered",
  "users.initial": "Initial",
  "users.noScheduleHistory": "No schedule has been registered.",
  "groups.description": "Permissions grouped by role.",
  "groups.search": "Search groups",
  "groups.members": "Members",
  "groups.permissions": "Permissions",
  "groups.permissionCount": "{{count}} permissions",
  "groups.type": "Type",
  "groups.protected": "Protected",
  "groups.custom": "Custom",
  "groups.saved": "Group saved.",
  "groups.deleted": "Group deleted.",
  "groups.deletePassword": "Confirm your password to delete this group:",
  "groups.deleteConfirm": "Delete {{name}}?",
  "groups.addTitle": "Add group",
  "groups.group": "Group",
  "groups.administrators": "Administrators",
  "groups.protectedDescription": "This group is protected.",
  "groups.formDescription": "Set the name and permissions.",
  "permissions.core.user.read": "View users",
  "permissions.core.user.create": "Invite and create users",
  "permissions.core.user.update": "Edit users",
  "permissions.core.user.delete": "Remove users and revoke invitations",
  "permissions.core.group.read": "View groups",
  "permissions.core.group.create": "Create groups",
  "permissions.core.group.update": "Edit groups",
  "permissions.core.group.delete": "Delete groups",
  "permissions.core.plugin.read": "View plugins",
  "permissions.core.plugin.create": "Install plugins",
  "permissions.core.plugin.update": "Update installed plugins",
  "permissions.core.plugin.delete": "Uninstall plugins",
  "permissions.core.plugin.export": "Download plugin packages",
  "permissions.core.marketplace.read": "View marketplaces",
  "permissions.core.marketplace.create": "Add marketplaces",
  "permissions.core.marketplace.update": "Update marketplaces",
  "permissions.core.marketplace.delete": "Remove marketplaces",
  "permissions.core.webhook.read": "View webhooks",
  "permissions.core.webhook.create": "Create webhooks",
  "permissions.core.webhook.update": "Edit webhooks and replace secrets",
  "permissions.core.webhook.delete": "Delete webhooks",
  "permissions.core.webhook.test": "Send test webhooks",
  "permissions.core.webhook.redeliver": "Resend failed webhooks",
  "permissions.core.audit.read": "View audit history",
  "permissions.core.settings.read": "View general settings",
  "permissions.core.settings.update": "Update Nexus Edge",
  "permissions.crm.lead.read": "View leads",
  "permissions.crm.lead.create": "Create leads",
  "permissions.crm.lead.update": "Edit leads",
  "permissions.crm.lead.delete": "Delete leads",
  "permissions.meta_ads.account.read": "View ad accounts",
  "permissions.meta_ads.account.create": "Add ad accounts",
  "permissions.meta_ads.account.update": "Edit ad accounts",
  "permissions.meta_ads.account.delete": "Delete ad accounts",
  "permissions.meta_ads.campaign.read": "View Meta campaigns",
  "permissions.meta_ads.campaign.update": "Pause and activate Meta campaigns",
  "permissions.meta_ads.adset.read": "View ad sets",
  "permissions.meta_ads.adset.update": "Pause and activate ad sets",
  "permissions.meta_ads.ad.read": "View Meta ads",
  "permissions.meta_ads.ad.update": "Pause and activate Meta ads",
  "permissions.meta_ads.insight.read": "View Meta Ads metrics",
  "permissions.soletrando.child.read": "View children and practice",
  "permissions.soletrando.child.create": "Register children",
  "permissions.soletrando.child.update": "Edit children and renew links",
  "permissions.soletrando.child.delete": "Delete children and histories",
  "permissions.additional": "Additional module permission",
  "permissionGroups.core.user": "Users",
  "permissionGroups.core.group": "Groups and access",
  "permissionGroups.core.plugin": "Plugins",
  "permissionGroups.core.marketplace": "Plugin marketplaces",
  "permissionGroups.core.webhook": "Webhooks",
  "permissionGroups.core.audit": "Audit",
  "permissionGroups.core.settings": "General settings",
  "permissionGroups.crm.lead": "CRM — Leads",
  "permissionGroups.meta_ads.account": "Meta Ads — Accounts",
  "permissionGroups.meta_ads.campaign": "Meta Ads — Campaigns",
  "permissionGroups.meta_ads.adset": "Meta Ads — Ad sets",
  "permissionGroups.meta_ads.ad": "Meta Ads — Ads",
  "permissionGroups.meta_ads.insight": "Meta Ads — Metrics",
  "permissionGroups.soletrando.child": "Spelling Practice — Children",
  "permissionGroups.additional": "Other module permissions",
  "settings.title": "General settings",
  "settings.coreCardsTitle": "Show system menus as cards",
  "settings.coreCardsDescription":
    "Show sidebar shortcuts on the home screen. Plugins remain visible.",
  "settings.coreCardsLoadFailed":
    "Unable to load this preference. Refresh the page to try again.",
  "settings.coreCardsSaveFailed": "Unable to save the card display preference.",
  "settings.description": "Installation information and Nexus Edge updates.",
  "settings.installationTitle": "Installation",
  "settings.installationDescription":
    "Version and environment currently running.",
  "settings.provider": "Database",
  "settings.channel": "Channel",
  "settings.channelBeta": "Beta",
  "settings.channelStable": "Stable",
  "settings.signature": "Signature",
  "settings.required": "Required",
  "settings.updatesTitle": "Updates",
  "settings.updatesDescription":
    "Checks signed beta releases directly from the official GitHub repository.",
  "settings.updateNow": "Update now",
  "settings.continueUpdate": "Continue update",
  "settings.confirmUpdatePassword":
    "Confirm your password to update Nexus Edge:",
  "settings.releaseDetails": "View release",
  "settings.noRelease": "No newer beta release has been published.",
  "settings.sourceUnavailable":
    "GitHub releases could not be checked or validated right now.",
  "settings.d1Only":
    "The beta automatic updater is available only for D1 installations.",
  "settings.credentialRequired":
    "Configure the limited Cloudflare credential first.",
  "settings.openPlugins": "Open Plugins",
  "settings.backupNotice":
    "Before migrations, Nexus records a D1 Time Travel restore point. The updater never accepts files without a valid signature.",
  "settings.keepPageOpen": "Keep this page open until verification finishes.",
  "settings.updateStageMigrating": "Applying signed migrations",
  "settings.updateStageDeploying": "Publishing Worker and interface",
  "settings.updateStageVerifying": "Verifying the new version",
  "settings.updateStageInstalled": "Update complete",
  "settings.updateStageFailed": "Update stopped",
  "settings.updateFailed":
    "The update stopped safely. Try again after checking the diagnostic.",
  "settings.updateInstalled": "Nexus Edge updated successfully.",
  "errors.CORE_UPDATE_STAGE_FAILED":
    "The update stage failed and stopped safely.",
  "errors.CORE_UPDATE_SOURCE_UNAVAILABLE":
    "GitHub releases could not be validated.",
  "errors.CORE_UPDATE_CREDENTIAL_REQUIRED":
    "Configure the limited Cloudflare credential on the Plugins page.",
  "errors.CORE_UPDATE_NOT_AVAILABLE": "This installation is already current.",
  "errors.CORE_UPDATE_PROVIDER_UNSUPPORTED":
    "The beta updater supports D1 installations only.",
  "apiKeys.description": "Personal credentials for scripts and agents.",
  "apiKeys.search": "Search keys",
  "apiKeys.unnamed": "Unnamed",
  "apiKeys.identifier": "Identifier",
  "apiKeys.active": "Active",
  "apiKeys.revoked": "Revoked",
  "apiKeys.createdSuccess": "Key created. Copy the secret now.",
  "apiKeys.revokedSuccess": "Key revoked.",
  "apiKeys.revokeConfirm": "Revoke this key?",
  "apiKeys.deleteLabel": "Delete key",
  "apiKeys.titleSingle": "API key",
  "apiKeys.lastUsed": "Last used",
  "apiKeys.addTitle": "Add key",
  "apiKeys.addDescription": "The full secret will be displayed only once.",
  "apiKeys.copied": "Copied.",
  "apiKeys.copySecret": "Copy secret",
  "apiKeys.validityDays": "Validity in days",
  "apiKeys.scopes": "Scopes",
  "apiKeys.create": "Create key",
  "apiKeys.documentation": "API documentation",
  "webhooks.description": "Endpoints, events, and signed deliveries.",
  "webhooks.search": "Search endpoints",
  "webhooks.destination": "Destination",
  "webhooks.events": "Events",
  "webhooks.eventTypes": "{{count}} types",
  "webhooks.saved": "Webhook saved.",
  "webhooks.deleted": "Webhook deleted.",
  "webhooks.deletePassword": "Confirm your password to delete:",
  "webhooks.rotatePassword": "Confirm your password to rotate the secret:",
  "webhooks.secretRotated": "Secret rotated.",
  "webhooks.actionQueued": "Action queued.",
  "webhooks.redeliveryQueued": "Redelivery queued.",
  "webhooks.test": "Test",
  "webhooks.recentDeliveries": "Recent deliveries",
  "webhooks.noDeliveries": "No deliveries",
  "webhooks.noDeliveriesDescription": "Delivery attempts will appear here.",
  "webhooks.event": "Event",
  "webhooks.attempts": "Attempts",
  "webhooks.redeliver": "Redeliver",
  "webhooks.deliveryStatus.delivered": "Delivered",
  "webhooks.deliveryStatus.failed": "Failed",
  "webhooks.deliveryStatus.pending": "Pending",
  "webhooks.addTitle": "Add webhook",
  "webhooks.copyOnce": "Copy the secret now. It will not be shown again.",
  "webhooks.copySecret": "Copy secret",
  "webhooks.httpsUrl": "HTTPS URL",
  "webhooks.urlPlaceholder": "https://example.com/webhook",
  "webhooks.enabled": "Active endpoint",
  "webhooks.rotate": "Rotate",
  "audit.description": "Administrative actions and requestId traceability.",
  "audit.search": "Search by action",
  "audit.noEvents": "No events",
  "audit.noEventsDescription": "Administrative actions will appear here.",
  "audit.action": "Action",
  "audit.resource": "Resource",
  "audit.authentication": "Authentication",
  "audit.record": "Audit record",
  "audit.user": "User",
  "audit.metadata": "Metadata",
  "plugins.description":
    "Install, update, and manage independent plugins and their marketplaces.",
  "plugins.installedSection": "Installed",
  "plugins.tabs.label": "Plugin sections",
  "plugins.tabs.installed": "Installed",
  "plugins.tabs.catalog": "New Plugins",
  "plugins.tabs.marketplaces": "Market Places",
  "plugins.explore": "Explore",
  "plugins.exploreDescription":
    "Install or update directly from verified GitHub marketplaces.",
  "plugins.searchMarketplace": "Search catalog",
  "plugins.publisher": "Publisher",
  "plugins.marketplace": "Marketplace",
  "plugins.compatibility": "Compatibility",
  "plugins.installationStatus": "Installation",
  "plugins.compatible": "Compatible",
  "plugins.incompatible": "Incompatible",
  "plugins.available": "Available",
  "plugins.alreadyInstalled": "Already installed",
  "plugins.updateAvailable": "Update available",
  "plugins.updateFromFile": "Update from file",
  "plugins.changelog": "What changed in this release",
  "plugins.noChangelog": "Release notes are not available for this version.",
  "plugins.updateVersions": "Version {{current}} to {{next}}",
  "plugins.updateIncompatible":
    "This version is not compatible with this installation.",
  "plugins.updateUnavailable":
    "This marketplace must be synchronized again before updating.",
  "plugins.packageSize": "Package size",
  "plugins.pluginDetails": "Plugin details",
  "plugins.catalogEmpty":
    "No plugins are available. Sync a marketplace to load the catalog.",
  "plugins.marketplaces": "Marketplaces",
  "plugins.marketplacesDescription":
    "Authorized GitHub sources for plugin discovery and updates.",
  "plugins.addMarketplace": "Add marketplace",
  "plugins.defaultMarketplace": "default",
  "plugins.repository": "GitHub repository",
  "plugins.trust": "Trust",
  "plugins.lastSync": "Last sync",
  "plugins.syncMarketplace": "Sync marketplace",
  "plugins.marketplaceAdded": "Marketplace added.",
  "plugins.marketplaceUpdated": "Marketplace updated.",
  "plugins.marketplaceSynced": "Marketplace synced and signature verified.",
  "plugins.marketplaceAutoSyncFailed":
    "One or more marketplaces could not be refreshed. The catalog shown may be out of date.",
  "plugins.marketplaceRemoved":
    "Marketplace removed; installed plugins were preserved.",
  "plugins.removeMarketplaceConfirm": "Remove marketplace {{name}}?",
  "plugins.marketplaceTrustNotice":
    "The catalog will sync automatically. Its signature is verified and the first valid public key is secured in this installation without manual confirmation.",
  "plugins.marketplaceDetails":
    "Edit the name, copy its details, or change this marketplace's availability.",
  "plugins.copyMarketplaceName": "Copy marketplace name",
  "plugins.copyMarketplaceRepository": "Copy marketplace repository",
  "plugins.copied": "Copied.",
  "plugins.copyFailed": "Unable to copy.",
  "plugins.marketplaceDownloadFailed":
    "The marketplace package could not be downloaded and verified.",
  "plugins.recoveryModeTitle": "Core recovery mode",
  "plugins.recoveryModeDescription":
    "This plugin's dynamic interface was not loaded. Open Plugins to disable, update, or inspect its source.",
  "plugins.dynamicUnavailableTitle": "Plugin interface unavailable",
  "plugins.dynamicUnavailableDescription":
    "The plugin is not installed, you do not have access, or its local assets are unavailable.",
  "plugins.dynamicFailedTitle": "The plugin interface encountered an error",
  "plugins.openRecoveryMode": "Open recovery mode",
  "plugins.search": "Search plugins",
  "plugins.database": "Database",
  "plugins.worker": "Worker",
  "plugins.installTitle": "Install or update plugin",
  "plugins.minimumCore": "Minimum Core",
  "plugins.rawSize": "Raw size",
  "plugins.migrations": "Migrations",
  "plugins.permissions": "Permissions",
  "plugins.operation": "Operation {{id}}",
  "plugins.update": "Update",
  "plugins.install": "Install",
  "plugins.uninstall": "Uninstall",
  "plugins.downloadPackage": "Download package",
  "plugins.packageDownloaded": "Plugin package downloaded.",
  "plugins.downloadUnavailable":
    "Select the original package once to verify it and enable download.",
  "plugins.selectOriginalPackage":
    "Select the original .plugin.zip used for this installation. It will be verified without reinstalling the plugin or changing data.",
  "plugins.archivePackageMismatch":
    "The selected file does not match the installed plugin and version.",
  "plugins.provider": "Provider",
  "plugins.invalidPackage": "Invalid package.",
  "plugins.permissionRequired":
    "Your group does not allow installing or updating this plugin.",
  "plugins.selectPackage": "Select a .plugin.zip file.",
  "plugins.packageContents":
    "The package must contain manifest.json and worker.mjs.",
  "plugins.migrationPairs": "D1 and PostgreSQL migrations must exist in pairs.",
  "plugins.rawTooLarge": "The raw package exceeds 8 MiB.",
  "plugins.expansionTooLarge":
    "The package exceeds the safe expansion or file-count limits.",
  "plugins.gzipTooLarge": "The compressed Worker exceeds 3 MiB.",
  "plugins.installFailed":
    "Installation failed. Open and copy the support report below.",
  "plugins.runtimeCredentialTitle": "Authorize the first plugin deployment",
  "plugins.runtimeCredentialBody":
    "This is required only once so Nexus can publish and update your plugin Workers.",
  "plugins.runtimeCredentialCreate": "Create token in Cloudflare",
  "plugins.runtimeCredentialOpenList": "Open this account's token list",
  "plugins.runtimeCredentialStepOpen":
    "Select the button above. Cloudflare will open the correct account with the token name and permission prefilled.",
  "plugins.runtimeCredentialStepReview":
    "Confirm that it has only this permission:",
  "plugins.runtimeCredentialStepCreateToken":
    "Select Continue to summary and then Create Token.",
  "plugins.runtimeCredentialStepCreate":
    "In Cloudflare, select Create Token and choose to create a Custom Token.",
  "plugins.runtimeCredentialStepPermission": "Add only this permission:",
  "plugins.runtimeCredentialStepAccount":
    "Under Account Resources, include only this account:",
  "plugins.runtimeCredentialStepPaste":
    "Copy the token that appears and paste it in the field below.",
  "plugins.runtimeCredentialTargetAccount": "Selected account:",
  "plugins.runtimeCredentialWarning":
    "Cloudflare displays the token only once. Do not send it in a message or save it in a document.",
  "plugins.runtimeCredentialLabel": "Dedicated API Token",
  "plugins.runtimeCredentialPlaceholder": "Paste the Cloudflare token here",
  "plugins.runtimeCredentialHelp":
    "The token is validated and written directly as a secret on this Core Worker. It is never stored in the database.",
  "plugins.runtimeCredentialLater": "Do this later",
  "plugins.runtimeCredentialSave": "Validate and save token",
  "plugins.runtimeCredentialContinue": "Validate token and install",
  "plugins.runtimeCredentialSaved": "Plugin credential configured.",
  "plugins.runtimeCredentialSavedAndInstalling":
    "Plugin credential configured. Starting installation…",
  "plugins.runtimeCredentialActivationPending":
    "Cloudflare saved the secret, but the new Worker version is still activating. Wait a few seconds and try installing again.",
  "plugins.runtimeCredentialLoadFailed":
    "The plugin credential status could not be checked.",
  "plugins.runtimeCredentialRetry": "Check again",
  "plugins.supportReport": "Installation support report",
  "plugins.supportReportHelp":
    "Copy this safe diagnostic and send it to the developer. Raw logs, credentials, and secrets are not included.",
  "plugins.copySupportReport": "Copy report",
  "plugins.supportReportCopied": "Support report copied.",
  "plugins.supportReportCopyFailed":
    "The report could not be copied. Select the text manually.",
  "plugins.installed": "Plugin installed.",
  "plugins.activated": "Plugin activated.",
  "plugins.deactivated": "Plugin deactivated without uninstalling.",
  "plugins.activateBeforeUpdate":
    "This plugin is disabled. Activate it before updating.",
  "plugins.uninstalled": "Plugin uninstalled; database tables were preserved.",
  "plugins.uninstallConfirm": "Uninstall {{name}} {{version}}?",
  "plugins.recordDeleted":
    "Plugin record deleted; tables and history were preserved.",
  "plugins.deleteRecordConfirm":
    "Delete {{name}} from the plugin list? Tables and history will be preserved.",
  "plugins.state.validating": "Validating",
  "plugins.state.provisioning": "Provisioning resources",
  "plugins.state.migrating": "Running migrations",
  "plugins.state.deploying": "Deploying",
  "plugins.state.hardening": "Hardening",
  "plugins.state.binding": "Binding",
  "plugins.state.registering": "Registering",
  "plugins.state.installed": "Installed",
  "plugins.state.disabled": "Disabled",
  "plugins.state.failed": "Failed",
  "plugins.r2ProvisioningTitle": "Private R2 storage",
  "plugins.r2ProvisioningDescription":
    "This plugin requires a dedicated bucket. Use a temporary token only to create and attach that bucket.",
  "plugins.r2ProvisioningStepPermission":
    "In My Profile → API Tokens, create a user token limited to Account → Workers R2 Storage → Edit for this account.",
  "plugins.r2ProvisioningStepPaste":
    "Paste the token below and complete the installation.",
  "plugins.r2ProvisioningStepRevoke":
    "Revoke the token in Cloudflare after installation completes.",
  "plugins.r2OpenTokens": "Open user API tokens",
  "plugins.r2TokenLabel": "Temporary R2 token",
  "plugins.r2TokenPlaceholder": "Paste the temporary token",
  "plugins.r2TokenPrivacy":
    "The token stays only in this screen's memory and is discarded after provisioning.",
  "plugins.r2TokenRequired": "Enter the temporary R2 token.",
  "plugins.resourceProvisioningTitle": "Private plugin resources",
  "plugins.resourceProvisioningDescription":
    "The manifest requests isolated Cloudflare resources. Core creates and records each resource without retaining the administrative token.",
  "plugins.resourceProvisioningStepPermission":
    "Create a temporary token limited to the permissions and products requested by the manifest.",
  "plugins.resourceProvisioningStepPaste":
    "Paste the token below; Core will provision only the pending resources.",
  "plugins.resourceProvisioningStepRevoke":
    "Revoke the token in Cloudflare as soon as the operation completes.",
  "plugins.resourceOpenTokens": "Open Cloudflare account tokens",
  "plugins.resourceTokenLabel": "Temporary resource token",
  "plugins.resourceTokenPlaceholder": "Paste the temporary token",
  "plugins.resourceTokenPrivacy":
    "The token is used only for this operation and is not stored or sent to the plugin.",
  "plugins.resourceTokenRequired":
    "Enter a temporary token limited to the products requested by the plugin.",
  "plugins.resourceReauthPassword":
    "Confirm your password to provision the plugin Cloudflare resources.",
  "plugins.resourcePlanInvalid":
    "The plugin resource plan is incomplete or inconsistent.",
  "plugins.marketplaceTrustTitle": "Trust marketplace key",
  "plugins.marketplaceTrustDescription":
    "Verify the fingerprint through a trusted channel and enter it to pin this publisher identity.",
  "plugins.marketplaceFingerprint": "Presented fingerprint",
  "plugins.marketplaceFingerprintConfirmation":
    "Confirm by entering the complete fingerprint",
  "plugins.marketplaceTrustConfirm": "Trust and synchronize",
  "plugins.marketplaceTrusted": "Marketplace trusted and synchronized.",
  "plugins.marketplaceTrustInvalid": "The key details are unavailable.",
  "plugins.marketplaceTrustReauthPassword":
    "Confirm your password to trust the marketplace key.",
  "plugins.r2ReauthPassword": "Confirm your password to provision R2",
  "errors.fallback": "Unable to complete the operation.",
  "errors.BOOTSTRAP_UNAVAILABLE": "Initial setup has already been completed.",
  "errors.BOOTSTRAP_ALREADY_CLAIMED":
    "Initial setup was already claimed by another email address.",
  "errors.BOOTSTRAP_RACE": "Another request completed this step.",
  "errors.VALIDATION_ERROR": "Review the submitted data.",
  "errors.UNAUTHORIZED": "Sign in to continue.",
  "errors.UNAUTHENTICATED": "Sign in to continue.",
  "errors.FORBIDDEN": "You do not have permission for this operation.",
  "errors.PLUGIN_PACKAGE_EXPORT_NOT_INSTALLED":
    "Only installed plugins can be downloaded.",
  "errors.PLUGIN_PACKAGE_EXPORT_UNAVAILABLE":
    "The portable package is unavailable. Select the original file to restore it safely.",
  "errors.PLUGIN_PACKAGE_ARCHIVE_MISMATCH":
    "The selected file is not exactly the package used for this installation.",
  "errors.PLUGIN_PACKAGE_CONTAINS_RUNTIME_VALUE":
    "The package contains configuration or credentials specific to this installation and cannot be installed.",
  "errors.PLUGIN_CORE_VERSION_UNSUPPORTED":
    "Update the Nexus Core before installing this plugin version.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_REQUIRED":
    "Configure the limited Cloudflare token before installing the plugin.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_INVALID":
    "The token is invalid, belongs to another account, or lacks Workers Scripts Edit.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_TOO_BROAD":
    "Use a dedicated token with only Workers Scripts Edit on this account.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_TARGET_MISSING":
    "This installation's Cloudflare account is not configured on the Core.",
  "errors.PLUGIN_RUNTIME_CREDENTIAL_SAVE_FAILED":
    "Cloudflare could not save the token as a secret on the Core Worker.",
  "errors.EMAIL_ALREADY_EXISTS": "This email already belongs to another user.",
  "errors.PASSWORD_ACCOUNT_UNAVAILABLE":
    "This user does not have a password account.",
  "errors.GROUP_UNAVAILABLE": "One or more selected groups do not exist.",
  "errors.ADMIN_GROUP_FORBIDDEN":
    "Only an administrator can assign the Administrators group.",
  "errors.PERMISSION_ESCALATION":
    "You cannot grant permissions beyond your own.",
  "errors.LAST_ADMIN":
    "The last active administrator must remain active and in the Administrators group.",
  "errors.REAUTH_REQUIRED": "Confirm your password to continue.",
  "errors.REAUTH_INVALID": "The password confirmation expired or is invalid.",
  "errors.USER_NOT_FOUND": "User not found.",
  "errors.META_TOKEN_NOT_CONFIGURED":
    "The Meta token has not been configured on the plugin Worker yet.",
  "errors.INVALID_AD_ACCOUNT_ID": "Enter a valid ad account ID.",
  "errors.AD_ACCOUNT_ALREADY_EXISTS": "This account is already configured.",
  "errors.AD_ACCOUNT_NOT_CONFIGURED":
    "The account is not configured or is disabled.",
  "errors.INVALID_DATE_RANGE": "Review the selected date range.",
  "errors.DATE_RANGE_TOO_LARGE": "Select no more than 366 days.",
};

const resources: Record<AppLocale, Record<TranslationKey, string>> = {
  "pt-BR": ptBR,
  en,
};

const storageKey = "modular.language";
const resolveLocale = (value?: string | null): AppLocale => {
  if (value === "pt-BR" || value?.toLowerCase().startsWith("pt"))
    return "pt-BR";
  if (value === "en" || value?.toLowerCase().startsWith("en")) return "en";
  return "pt-BR";
};

let activeLocale: AppLocale = resolveLocale(
  typeof window === "undefined"
    ? undefined
    : (localStorage.getItem(storageKey) ?? navigator.languages[0]),
);

const interpolate = (
  message: string,
  values?: Record<string, string | number>,
) =>
  message.replace(/\{\{(\w+)\}\}/gu, (_, key: string) =>
    String(values?.[key] ?? `{{${key}}}`),
  );

export const translate = (
  key: TranslationKey,
  values?: Record<string, string | number>,
): string => {
  const localized = resources[activeLocale] as Record<
    string,
    string | undefined
  >;
  const fallback = ptBR as Record<string, string | undefined>;
  return interpolate(localized[key] ?? fallback[key] ?? String(key), values);
};

export const hasTranslation = (key: string): key is TranslationKey =>
  Object.prototype.hasOwnProperty.call(ptBR, key);

export const getAppLocale = (): AppLocale => activeLocale;

type I18nValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  t: typeof translate;
  formatDate: (value: string | number | Date) => string;
  formatDateTime: (value: string | number | Date) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(activeLocale);
  const setLocale = useCallback((next: AppLocale) => {
    activeLocale = next;
    localStorage.setItem(storageKey, next);
    document.documentElement.lang = next;
    setLocaleState(next);
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: translate,
      formatDate: (input) => new Date(input).toLocaleDateString(locale),
      formatDateTime: (input) => new Date(input).toLocaleString(locale),
    }),
    [locale, setLocale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
